import {
  buildEmailIssueLookup,
  collectEmailIssuesForPeople,
  normalizeEmailAddress,
  summarizeEmailIssueGroups,
} from './emailHealthSignals';

const HOUR_MS = 60 * 60 * 1000;
const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const getTimeValue = (value) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDate = (value) => {
  if (!value) return 'Date pending';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Date pending';
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatDateTime = (value) => {
  if (!value) return 'Date pending';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Date pending';
  return parsed.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const hoursSince = (value) => {
  const timestamp = getTimeValue(value);
  if (!timestamp) return null;
  return Math.max(0, Math.floor((Date.now() - timestamp) / HOUR_MS));
};

const hoursUntil = (value) => {
  const timestamp = getTimeValue(value);
  if (!timestamp) return null;
  return Math.max(0, Math.ceil((timestamp - Date.now()) / HOUR_MS));
};

const guideDisplayName = (guide = {}) => guide?.name || guide?.email || 'Unnamed Guide Partner';

const normalizeInviteStage = (guide = {}) => (guide?.invite_stage || 'not_invited').trim().toLowerCase() || 'not_invited';

const normalizeStatus = (guide = {}) => (guide?.status || 'prospect').trim().toLowerCase() || 'prospect';

const isActiveCampaign = (campaign = {}) => ['scheduled', 'draft_ready', 'running', 'paused'].includes((campaign?.status || '').toLowerCase());

const toGuideItem = (guide, detail, tone = 'steady', extras = {}) => ({
  id: guide?.guide_id || `${guide?.email || 'guide'}-${detail}`,
  title: guideDisplayName(guide),
  detail,
  tone,
  ...extras,
});

export function deriveGuidePartnersState(guideSummary = null) {
  const guides = Array.isArray(guideSummary?.guides) ? guideSummary.guides : [];
  const activeCampaigns = Array.isArray(guideSummary?.activeCampaigns)
    ? guideSummary.activeCampaigns.filter(isActiveCampaign)
    : [];

  const stepGuideSets = {
    email_1: new Set(),
    email_2: new Set(),
    email_3: new Set(),
  };
  const activeGuideIds = new Set();

  activeCampaigns.forEach((campaign) => {
    const stepKey = (campaign?.step_key || '').trim().toLowerCase();
    const recipientIds = Array.isArray(campaign?.recipient_ids) ? campaign.recipient_ids : [];
    recipientIds.forEach((guideId) => {
      activeGuideIds.add(guideId);
      if (stepGuideSets[stepKey]) {
        stepGuideSets[stepKey].add(guideId);
      }
    });
  });

  const notInvited = guides.filter((guide) => normalizeInviteStage(guide) === 'not_invited');
  const invited = guides.filter((guide) => normalizeInviteStage(guide) === 'invited');
  const onboardingSent = guides.filter((guide) => normalizeInviteStage(guide) === 'onboarding_sent');
  const onboardingCompleted = guides.filter((guide) => normalizeInviteStage(guide) === 'onboarding_completed');
  const scheduled = guides.filter((guide) => normalizeInviteStage(guide) === 'scheduled' || normalizeStatus(guide) === 'scheduled');
  const activePartners = guides.filter((guide) => normalizeStatus(guide) === 'active');

  const safeToInviteNow = notInvited.filter((guide) => !activeGuideIds.has(guide.guide_id));
  const invitedNotInSequence = invited.filter((guide) => !activeGuideIds.has(guide.guide_id));
  const recentFinalTouch = invitedNotInSequence.filter((guide) => {
    const lastStep = (guide?.last_outreach_step_key || '').trim().toLowerCase();
    const sentHoursAgo = hoursSince(guide?.last_outreach_sent_at);
    return lastStep === 'email_3' && sentHoursAgo !== null && sentHoursAgo <= 72;
  });

  const onboardingSentOverdue = onboardingSent.filter((guide) => {
    const sentHoursAgo = hoursSince(guide?.onboarding_sent_at);
    return sentHoursAgo !== null && sentHoursAgo >= 48 && !guide?.onboarding_completed_at && !guide?.scheduled_date;
  });
  const onboardingReminderSent = onboardingSent.filter((guide) => !!guide?.onboarding_reminder_sent_at && !guide?.onboarding_completed_at);
  const onboardingSentFresh = onboardingSent.filter((guide) => !onboardingSentOverdue.includes(guide) && !onboardingReminderSent.includes(guide));

  const readyToSchedule = [...onboardingCompleted].sort(
    (a, b) => getTimeValue(a?.onboarding_completed_at) - getTimeValue(b?.onboarding_completed_at)
  );
  const scheduledSorted = [...scheduled].sort(
    (a, b) => getTimeValue(a?.scheduled_date) - getTimeValue(b?.scheduled_date)
  );
  const activePartnersSorted = [...activePartners].sort(
    (a, b) => getTimeValue(b?.updated_at || b?.created_at) - getTimeValue(a?.updated_at || a?.created_at)
  );
  const invitedSorted = [...invitedNotInSequence].sort(
    (a, b) => getTimeValue(a?.last_outreach_sent_at) - getTimeValue(b?.last_outreach_sent_at)
  );

  return {
    guides,
    activeCampaigns,
    activeGuideIds,
    counts: {
      total: guides.length,
      notInvited: notInvited.length,
      safeToInviteNow: safeToInviteNow.length,
      invited: invited.length,
      invitedNotInSequence: invitedNotInSequence.length,
      onboardingSent: onboardingSent.length,
      onboardingSentOverdue: onboardingSentOverdue.length,
      onboardingReminderSent: onboardingReminderSent.length,
      onboardingCompleted: onboardingCompleted.length,
      scheduled: scheduled.length,
      activePartners: activePartners.length,
      activeSequence: activeGuideIds.size,
      queuedEmail1: stepGuideSets.email_1.size,
      queuedEmail2: stepGuideSets.email_2.size,
      queuedEmail3: stepGuideSets.email_3.size,
      recentFinalTouch: recentFinalTouch.length,
    },
    stageLists: {
      safeToInviteNow,
      invitedNotInSequence,
      onboardingSent,
      onboardingSentOverdue,
      onboardingSentFresh,
      onboardingReminderSent,
      readyToSchedule,
      scheduled: scheduledSorted,
      activePartners: activePartnersSorted,
      recentFinalTouch,
      invitedSorted,
    },
  };
}

export function buildGuidePartnersRoomContext({ room, guideSummary, emailDeliveryIssues = [], primaryAction, secondaryAction }) {
  const derived = deriveGuidePartnersState(guideSummary);
  const counts = derived.counts;
  const lists = derived.stageLists;
  const emailIssueLookup = buildEmailIssueLookup(emailDeliveryIssues);
  const guideEmailIssues = collectEmailIssuesForPeople(
    derived.guides,
    (guide) => emailIssueLookup.get(normalizeEmailAddress(guide?.email))
  );
  const emailIssueGroups = summarizeEmailIssueGroups(guideEmailIssues);
  const emailIssueCount = guideEmailIssues.length;

  const statusTone = emailIssueCount > 0
    ? 'attention'
    : counts.onboardingCompleted > 0 || counts.onboardingSentOverdue > 0
    ? 'attention'
    : counts.safeToInviteNow > 0
      ? 'next'
      : counts.activeSequence > 0
        ? 'steady'
        : 'steady';

  const statusLabel = emailIssueCount > 0
    ? 'Email cleanup'
    : counts.onboardingCompleted > 0
    ? 'Needs Kevin'
    : counts.onboardingSentOverdue > 0
      ? 'Waiting on them'
      : counts.safeToInviteNow > 0
        ? 'Ready to invite'
        : counts.activeSequence > 0
          ? 'Sequence active'
          : 'Build next';

  const startTitle = emailIssueCount > 0
    ? `Correct ${pluralize(emailIssueCount, 'Guide Partner email')} before assuming outreach landed`
    : counts.onboardingCompleted > 0
    ? `Schedule ${pluralize(counts.onboardingCompleted, 'Guide Partner')} who completed onboarding`
    : counts.onboardingSentOverdue > 0
      ? `${pluralize(counts.onboardingSentOverdue, 'Guide Partner')} need the next nudge on onboarding`
      : counts.safeToInviteNow > 0
        ? `${pluralize(counts.safeToInviteNow, 'fresh Guide Partner')} are safe to invite now`
        : counts.activeSequence > 0
          ? `${pluralize(counts.activeSequence, 'Guide Partner')} are already moving through the invite sequence`
          : 'Keep the Guide Partner pipeline moving with clear next steps';

  const startDetail = emailIssueCount > 0
    ? `A bad address can make the sequence look active when the person never actually received the note. Fix current email problems first, then decide who to invite, remind, or schedule.`
    : counts.activeSequence > 0
    ? `There are already ${pluralize(counts.queuedEmail1, 'first touch')}, ${pluralize(counts.queuedEmail2, 'second touch')}, and ${pluralize(counts.queuedEmail3, 'final touch')} queued. Use this room to avoid stepping on that sequence and to see who is waiting on Kevin versus waiting on them.`
    : 'This room should show where each potential Guide Partner sits, what the next step is, and whether the ball is in their court or yours.';

  const priorityItems = [];
  if (emailIssueCount > 0) {
    priorityItems.push({
      tone: 'attention',
      eyebrow: 'Blocked outreach',
      title: `Correct ${pluralize(emailIssueCount, 'current Guide Partner email issue')}`,
      detail: guideEmailIssues
        .slice(0, 2)
        .map(({ person }) => guideDisplayName(person))
        .join(', ') || 'These names need cleaner email paths before the sequence can help them.',
      location: 'In Guide Partners',
    });
  }
  if (counts.onboardingCompleted > 0) {
    priorityItems.push({
      tone: 'attention',
      eyebrow: 'Do first',
      title: `Personally schedule ${pluralize(counts.onboardingCompleted, 'Guide Partner')}`,
      detail: lists.readyToSchedule
        .slice(0, 2)
        .map((guide) => guideDisplayName(guide))
        .join(', ') || 'Reach out to the guides who already completed the form.',
      location: 'In Guide Partners',
    });
  }
  if (counts.onboardingSentOverdue > 0) {
    priorityItems.push({
      tone: 'attention',
      eyebrow: 'Waiting on them',
      title: `${pluralize(counts.onboardingSentOverdue, 'Guide Partner')} are overdue on onboarding`,
      detail: counts.onboardingReminderSent > 0
        ? `${pluralize(counts.onboardingReminderSent, 'Guide Partner')} already got the reminder.`
        : 'These are past the 48-hour reminder window and should not silently sit.',
      location: 'In Guide Partners',
    });
  }
  if (counts.safeToInviteNow > 0) {
    priorityItems.push({
      tone: 'next',
      eyebrow: 'Open now',
      title: `Invite ${pluralize(counts.safeToInviteNow, 'fresh Guide Partner')} without colliding with the sequence`,
      detail: lists.safeToInviteNow
        .slice(0, 2)
        .map((guide) => guideDisplayName(guide))
        .join(', ') || 'Only untouched, non-queued names belong here.',
      location: 'In Guide Partners',
    });
  }
  if (!priorityItems.length) {
    priorityItems.push({
      tone: 'steady',
      eyebrow: 'Steady',
      title: 'The current Guide Partner pipeline is quiet',
      detail: 'Use the room columns to decide whether to invite, follow up, or schedule the next person.',
      location: 'In Guide Partners',
    });
  }

  const sequenceStats = [
    {
      key: 'email_health',
      label: 'Email health issues',
      value: emailIssueCount,
      tone: emailIssueCount > 0 ? 'attention' : 'steady',
      caption: emailIssueCount > 0
        ? `${pluralize(emailIssueGroups.needsCorrection, 'address')} need correction, ${pluralize(emailIssueGroups.doNotEmail, 'complaint')} need manual care`
        : 'No current delivery blocks are imported for Guide Partners',
    },
    {
      key: 'safe',
      label: 'Safe to invite now',
      value: counts.safeToInviteNow,
      tone: counts.safeToInviteNow > 0 ? 'next' : 'steady',
      caption: counts.queuedEmail1 > 0
        ? `${pluralize(counts.queuedEmail1, 'Guide Partner')} already queued for first touch`
        : 'No untouched names are already queued',
    },
    {
      key: 'email_1',
      label: 'First touch queued',
      value: counts.queuedEmail1,
      tone: counts.queuedEmail1 > 0 ? 'attention' : 'steady',
      caption: 'New names who already have Email 1 scheduled',
    },
    {
      key: 'email_2',
      label: 'Second touch queued',
      value: counts.queuedEmail2,
      tone: counts.queuedEmail2 > 0 ? 'attention' : 'steady',
      caption: 'People already set to receive the circle-back note',
    },
    {
      key: 'email_3',
      label: 'Final touch queued',
      value: counts.queuedEmail3,
      tone: counts.queuedEmail3 > 0 ? 'attention' : 'steady',
      caption: 'People already set to receive the final note',
    },
  ];

  const stageColumns = [
    {
      key: 'not_invited',
      label: 'Not Invited',
      count: counts.notInvited,
      emphasisCount: counts.safeToInviteNow,
      emphasisLabel: 'Safe now',
      tone: 'sand',
      ownerLabel: counts.safeToInviteNow > 0 ? 'Needs Kevin' : 'Covered',
      ownerTone: counts.safeToInviteNow > 0 ? 'attention' : 'steady',
      description: 'Potential Guide Partners who have not been invited yet.',
      nextAction: counts.safeToInviteNow > 0 ? 'Choose who to invite next' : 'No clean fresh names left today',
      helper: counts.queuedEmail1 > 0
        ? `${pluralize(counts.safeToInviteNow, 'name')} are safe now. ${pluralize(counts.queuedEmail1, 'name')} already have first touch queued.`
        : `${pluralize(counts.safeToInviteNow, 'name')} are safe to invite right now.`,
      itemsLabel: counts.safeToInviteNow > 0
        ? `Showing up to 5 safe examples from ${pluralize(counts.notInvited, 'total name')} in this stage`
        : `All ${pluralize(counts.notInvited, 'name')} in this stage are already covered or queued`,
      items: lists.safeToInviteNow.slice(0, 5).map((guide) =>
        toGuideItem(guide, guide?.email || 'Fresh invite candidate', 'next')
      ),
    },
    {
      key: 'invited',
      label: 'Invited',
      count: counts.invited,
      emphasisCount: counts.invitedNotInSequence,
      emphasisLabel: 'Outside queue',
      tone: 'amber',
      ownerLabel: counts.invitedNotInSequence > 0 ? 'Watch timing' : 'Sequence active',
      ownerTone: counts.invitedNotInSequence > 0 ? 'attention' : 'steady',
      description: 'People already invited into the story-sharing path.',
      nextAction: counts.recentFinalTouch > 0 ? 'Do not crowd the final touch' : 'Review invited names outside the active sequence',
      helper: counts.invitedNotInSequence > 0
        ? `${pluralize(counts.invitedNotInSequence, 'invited guide')} are not in an active sequence right now.`
        : 'Every invited guide is either in a live sequence or already moved forward.',
      itemsLabel: counts.invitedNotInSequence > 0
        ? `Showing up to 5 invited people who are not already in the queued sequence`
        : `All ${pluralize(counts.invited, 'invited guide')} are already covered by a live or completed flow`,
      items: lists.invitedSorted.slice(0, 5).map((guide) =>
        toGuideItem(
          guide,
          guide?.last_outreach_step_key
            ? `Last touch ${String(guide.last_outreach_step_key).replace('_', ' ')} on ${formatDateTime(guide?.last_outreach_sent_at)}`
            : 'Invite sent with no recorded sequence step',
          guide?.last_outreach_step_key === 'email_3' ? 'attention' : 'steady'
        )
      ),
    },
    {
      key: 'onboarding_sent',
      label: 'Onboarding Sent',
      count: counts.onboardingSent,
      emphasisCount: counts.onboardingSentOverdue,
      emphasisLabel: 'Past 48h',
      tone: 'sky',
      ownerLabel: 'Waiting on them',
      ownerTone: counts.onboardingSentOverdue > 0 ? 'attention' : 'next',
      description: 'They received the form and the next move is theirs.',
      nextAction: counts.onboardingSentOverdue > 0 ? 'Let the reminder go, then follow up personally if needed' : 'Give them time, then watch for completion',
      helper: counts.onboardingSentOverdue > 0
        ? `${pluralize(counts.onboardingSentOverdue, 'guide')} are past 48 hours. ${pluralize(counts.onboardingReminderSent, 'guide')} already got the reminder.`
        : `${pluralize(lists.onboardingSentFresh.length, 'guide')} are still inside the normal response window.`,
      itemsLabel: counts.onboardingSent > 0
        ? 'Showing the people currently waiting on the form'
        : 'No Guide Partners are sitting in this handoff right now',
      items: lists.onboardingSent.slice(0, 5).map((guide) => {
        const overdue = lists.onboardingSentOverdue.some((item) => item.guide_id === guide.guide_id);
        const reminderSent = !!guide?.onboarding_reminder_sent_at;
        const ageHours = hoursSince(guide?.onboarding_sent_at);
        const detail = reminderSent
          ? `Reminder sent ${formatDateTime(guide?.onboarding_reminder_sent_at)}`
          : overdue
            ? `Form sent ${ageHours}h ago · reminder due`
            : `Form sent ${ageHours ?? 0}h ago`;
        return toGuideItem(guide, detail, overdue ? 'attention' : reminderSent ? 'steady' : 'next');
      }),
    },
    {
      key: 'ready_to_schedule',
      label: 'Ready to Schedule',
      count: counts.onboardingCompleted,
      emphasisCount: counts.onboardingCompleted,
      emphasisLabel: 'Needs Kevin',
      tone: 'teal',
      ownerLabel: counts.onboardingCompleted > 0 ? 'Needs Kevin' : 'Clear',
      ownerTone: counts.onboardingCompleted > 0 ? 'attention' : 'steady',
      description: 'They completed the form, and now the next move is yours.',
      nextAction: counts.onboardingCompleted > 0 ? 'Reach out personally and schedule their Sunday story' : 'No completed forms are waiting on you right now',
      helper: 'This is the cleanest handoff point back to Kevin.',
      itemsLabel: counts.onboardingCompleted > 0
        ? 'Showing the people who already did their part'
        : 'No completed onboarding forms are waiting on Kevin right now',
      items: lists.readyToSchedule.slice(0, 5).map((guide) =>
        toGuideItem(
          guide,
          `Completed onboarding ${formatDateTime(guide?.onboarding_completed_at)}`,
          'attention'
        )
      ),
    },
    {
      key: 'scheduled',
      label: 'Scheduled',
      count: counts.scheduled,
      emphasisCount: counts.scheduled,
      emphasisLabel: 'Upcoming',
      tone: 'violet',
      ownerLabel: counts.scheduled > 0 ? 'Prepare' : 'Quiet',
      ownerTone: counts.scheduled > 0 ? 'next' : 'steady',
      description: 'Their date is set and the work shifts to preparation and support.',
      nextAction: counts.scheduled > 0 ? 'Make sure they are ready to serve well' : 'No scheduled stories right now',
      helper: 'These people should feel supported, not forgotten between scheduling and service.',
      itemsLabel: counts.scheduled > 0
        ? 'Showing the next scheduled stories'
        : 'No Guide Partners are currently scheduled',
      items: lists.scheduled.slice(0, 5).map((guide) =>
        toGuideItem(
          guide,
          guide?.scheduled_date ? `Scheduled for ${formatDate(guide.scheduled_date)}` : 'Scheduled date pending',
          'next'
        )
      ),
    },
    {
      key: 'active',
      label: 'Active Partners',
      count: counts.activePartners,
      emphasisCount: counts.activePartners,
      emphasisLabel: 'Serving',
      tone: 'green',
      ownerLabel: counts.activePartners > 0 ? 'Steady' : 'Build next',
      ownerTone: 'steady',
      description: 'People already serving who may be able to encourage dads again.',
      nextAction: counts.activePartners > 0 ? 'Consider repeat service, mentoring, or partner roles' : 'No active repeat partners yet',
      helper: 'This is where Guide Partners become true partners in the mission.',
      itemsLabel: counts.activePartners > 0
        ? 'Showing the currently active partner bench'
        : 'No active repeat partners yet',
      items: lists.activePartners.slice(0, 5).map((guide) =>
        toGuideItem(
          guide,
          guide?.scheduled_topic || guide?.primary_focus_area || 'Ready to serve again',
          'steady'
        )
      ),
    },
  ];

  const watchListItems = [
    ...guideEmailIssues.slice(0, 2).map(({ person, issue, meta }) =>
      toGuideItem(person, `${meta.label} · ${meta.actionLabel} · ${issue?.email || normalizeEmailAddress(person?.email)}`, 'attention', {
        stageLabel: meta.actionLabel,
        stageTone: meta.tone,
      })
    ),
    ...lists.readyToSchedule.slice(0, 2).map((guide) =>
      toGuideItem(guide, 'Waiting on Kevin to schedule', 'attention', {
        stageLabel: 'Needs Kevin',
        stageTone: 'attention',
      })
    ),
    ...lists.onboardingSentOverdue.slice(0, 2).map((guide) =>
      toGuideItem(guide, 'Waiting on them past the reminder window', 'attention', {
        stageLabel: 'Onboarding overdue',
        stageTone: 'attention',
      })
    ),
    ...lists.safeToInviteNow.slice(0, 2).map((guide) =>
      toGuideItem(guide, 'Fresh name not already in the sequence', 'next', {
        stageLabel: 'Safe to invite',
        stageTone: 'next',
      })
    ),
  ].slice(0, 5);

  const pipelineDefaultExpanded = (
    emailIssueCount === 0 &&
    counts.onboardingCompleted === 0 &&
    counts.onboardingSentOverdue === 0 &&
    (
      counts.activeSequence > 0 ||
      counts.invitedNotInSequence > 0 ||
      counts.onboardingSent > 0 ||
      counts.scheduled > 0
    )
  );

  return {
    id: room.id,
    variant: 'guide-partners',
    statusTone,
    statusLabel,
    startTitle,
    startDetail,
    signals: [
      {
        tone: emailIssueCount > 0 ? 'attention' : 'steady',
        text: emailIssueCount > 0
          ? `${pluralize(emailIssueCount, 'Guide Partner')} have current email delivery problems and may not have actually received the note`
          : 'No current Guide Partner email delivery problems are imported right now.',
      },
      {
        tone: counts.activeSequence > 0 ? 'attention' : 'steady',
        text: counts.activeSequence > 0
          ? `${pluralize(counts.activeSequence, 'Guide Partner')} are already in a queued invite sequence`
          : 'No invite sequence is currently queued.',
      },
      {
        tone: counts.onboardingCompleted > 0 ? 'attention' : counts.onboardingSent > 0 ? 'next' : 'steady',
        text: counts.onboardingCompleted > 0
          ? `${pluralize(counts.onboardingCompleted, 'Guide Partner')} already did their part and are waiting on Kevin`
          : counts.onboardingSent > 0
            ? `${pluralize(counts.onboardingSent, 'Guide Partner')} still need to complete the onboarding form`
            : 'No onboarding handoffs are sitting right now.',
      },
    ],
    priorityItems,
    primaryAction,
    secondaryAction,
    sequenceStats,
    pipelineDefaultExpanded,
    stageColumns,
    watchListItems,
  };
}
